import { Component, inject, Input, ViewChild, OnInit, SimpleChanges, OnChanges } from '@angular/core';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { DataTableBuilderComponent } from '../../../components/data-table-builder/data-table-builder.component';
import { DialogComponent } from '../../../components/dialog/dialog.component';
import { DialogObject } from '@/components/dialog/dialog.types';
import { DataTableRowActions, DynamicDataTable } from '@/components/data-table-builder/data-table.types';
import { SettingsSeuilsService } from './settings-seuils.service';
import { getSeuilDataTableStructure } from './utils/seuil-data-table-object';
import { SettingsSeuilsRepository } from '@/store/seuils-state/seuils-state.repository';
import { DynamicForm } from '@/components/form-builder/form-builder.types';
import { getAddSeuilDialogObject } from './utils/seuil-add-dialog-object';
import { getSeuilFormObject } from './utils/seuil-data-form-object';
import { getUpdateSeuilDialogObject } from './utils/seuil-update-dialog-object';
import { map, Observable } from 'rxjs';
import { AuthorizationService } from '@/services/authorization.service';
import { UserRole } from '@/types/user.types';
import { RefParamsService } from '../ref-params/ref-params.service';
import { ContractingAuthorityService } from '../contracting-authority/contracting-authority.service';
import { RefTypeEnum } from '@/types';
import { CommonModule } from '@angular/common';

interface Seuil {
  id: {
    ac: any;
    typeMarcher: any;
  };
  seuil: number;
}

interface SelectOption {
  name: string;
  code: number;
}

@Component({
  selector: 'sm-settings-seuils',
  imports: [DataTableBuilderComponent, DialogComponent, TranslateModule, CommonModule],
  templateUrl: './settings-seuils.component.html',
  styleUrl: './settings-seuils.component.scss',
})
export class SettingsSeuilsComponent implements OnInit, OnChanges {
  settingsSeuilsService = inject(SettingsSeuilsService);
  settingsSeuilsRepository = inject(SettingsSeuilsRepository);
  messageService = inject(MessageService);
  translate = inject(TranslateService);
  authService = inject(AuthorizationService);
  refParamsService = inject(RefParamsService);
  contractingAuthorityService = inject(ContractingAuthorityService);

  @Input() acId?: number;
  @Input() isInspectMode?: boolean;

  typeMarcherOptions: SelectOption[] = [];
  optionsLoaded = false;

  isAddDisabled$: Observable<boolean> = this.settingsSeuilsRepository.getNestedObservable<number>('dto', 'seuil').pipe(
    map(seuil => {
      return !seuil || seuil <= 0;
    })
  );
  isUpdateDisabled$: Observable<boolean> = this.settingsSeuilsRepository.getNestedObservable<number>('dto', 'seuil').pipe(
    map(seuil => {
      return this.settingsSeuilsRepository.get('serverState') === seuil;
    })
  );

  seuilFormObject: DynamicForm = getSeuilFormObject({
    store: this.settingsSeuilsRepository,
    typeMarcherOptions: [],
  });

  @ViewChild('delete') deleteDialogComponent!: DialogComponent;
  deleteDialog?: DialogObject;

  @ViewChild('addDialog') addSeuilComponent!: DialogComponent;
  addSeuilDialog?: DialogObject;

  @ViewChild('updateDialog') updateSeuilComponent!: DialogComponent;
  updateSeuilDialog?: DialogObject;

  dataTableObject?: DynamicDataTable;
  data: Seuil[] = [];
  loading = false;

  getRowActions(): DataTableRowActions {
    return {
      editAction: this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN, UserRole.ADMIN_AC])
        ? () => {
            if (this.optionsLoaded) {
              const currentDto = this.settingsSeuilsRepository.get('dto') as any;
              const currentResponse = this.settingsSeuilsRepository.get('response') as any;

              if (!currentResponse || !currentResponse.id) {
                this.messageService.add({
                  severity: 'error',
                  summary: 'Erreur',
                  detail: this.translate.instant('settings.seuils.message.error.loading'),
                });
                return;
              }

              this.settingsSeuilsRepository.set('serverState', {
                seuil: currentDto.seuil,
                typeMarcherId: currentDto.typeMarcherId,
              });
              this.updateSeuilDialog = getUpdateSeuilDialogObject({
                formObject: this.createUpdateFormObject(),
                action: () => this.updateSeuil(),
                toggleVisibility: () => {
                  this.settingsSeuilsRepository.reset();
                  this.updateSeuilComponent.toggleVisible();
                },
                isUpdateDisabled: this.isUpdateDisabled$,
              });
              this.updateSeuilComponent.toggleVisible();
            } else {
              this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: this.translate.instant('settings.seuils.message.loading'),
              });
            }
          }
        : undefined,
    };
  }

  ngOnInit(): void {
    // Load both options together and create dialog only after both are loaded
    this.refParamsService.getSpecializedRefParams(RefTypeEnum.MARKET_TYPE).subscribe({
      next: (refs: any) => {
        this.typeMarcherOptions = refs.map((ref: any) => ({ name: ref.description, code: ref.id }));
        this.checkOptionsLoaded();
      },
      error: () => {
        this.typeMarcherOptions = [];
        this.checkOptionsLoaded();
      },
    });

    this.dataTableObject = getSeuilDataTableStructure({
      store: this.settingsSeuilsRepository,
      createAction: this.isInspectMode
        ? undefined
        : () => {
            if (this.optionsLoaded) {
              this.settingsSeuilsRepository.reset();
              this.addSeuilComponent.toggleVisible();
            } else {
              this.messageService.add({
                severity: 'warn',
                summary: 'Attention',
                detail: this.translate.instant('settings.seuils.message.loading'),
              });
            }
          },
      rowActions: this.isInspectMode ? undefined : this.getRowActions(),
    });
  }

  checkOptionsLoaded() {
    this.optionsLoaded = true;
    this.updateFormObject();
    this.createAddDialog();
  }

  createAddDialog() {
    this.addSeuilDialog = getAddSeuilDialogObject({
      formObject: this.seuilFormObject, // Use the real form object
      action: () => this.addSeuil(),
      isAddDisabled: this.isAddDisabled$,
      toggleVisibility: () => this.addSeuilComponent.toggleVisible(),
    });
  }

  updateFormObject() {
    this.seuilFormObject = getSeuilFormObject({
      store: this.settingsSeuilsRepository,
      typeMarcherOptions: this.typeMarcherOptions,
    });
  }

  createUpdateFormObject() {
    return getSeuilFormObject({
      store: this.settingsSeuilsRepository,
      typeMarcherOptions: this.typeMarcherOptions,
      isUpdateMode: true, // Disable select fields in update mode
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['acId'] && this.acId) {
      this.getSeuils();
    }
  }

  getSeuils() {
    this.loading = true;
    this.settingsSeuilsService.getSeuils(this.acId!).subscribe({
      next: response => {
        this.data = response.content;
        this.loading = false;
        this.dataTableObject = getSeuilDataTableStructure({
          store: this.settingsSeuilsRepository,
          createAction:
            this.isInspectMode || !this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN, UserRole.ADMIN_AC])
              ? undefined
              : () => {
                  this.settingsSeuilsRepository.reset();
                  this.addSeuilComponent.toggleVisible();
                },
          rowActions: this.getRowActions(),
        });
      },
      error: error => {
        // Handle the case when no seuils are found (500 error)
        if (error.status === 500 && error.error?.message?.includes('AcSeuil not found')) {
          this.data = [];
          this.loading = false;
          this.dataTableObject = getSeuilDataTableStructure({
            store: this.settingsSeuilsRepository,
            createAction:
              this.isInspectMode || !this.authService.hasRoles([UserRole.ADMIN_CENTRAL, UserRole.SUPER_ADMIN, UserRole.ADMIN_AC])
                ? undefined
                : () => {
                    this.settingsSeuilsRepository.reset();
                    this.addSeuilComponent.toggleVisible();
                  },
            rowActions: this.getRowActions(),
          });
        } else {
          this.loading = false;
        }
      },
    });
  }

  addSeuil() {
    const dto = this.settingsSeuilsRepository.get('dto') as any;
    // Ensure acId is included in the DTO
    const seuilDto = {
      ...dto,
      acId: this.acId,
    };

    this.settingsSeuilsService.addSeuil(seuilDto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.seuils.message.success.added'),
        });
        this.addSeuilComponent.toggleVisible();
        this.getSeuils();
      },
      error: err => {
        if (err.status === 400 && err.error.message.includes('exist')) {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.seuils.message.error.duplicated'),
          });
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Erreur',
            detail: this.translate.instant('settings.seuils.message.error.added'),
          });
        }
      },
    });
  }

  updateSeuil() {
    const seuil = this.settingsSeuilsRepository.get('response') as any;
    const dto = this.settingsSeuilsRepository.get('dto') as any;
    let typeMarcherId, modePassationId;
    if (seuil && seuil.id) {
      typeMarcherId = seuil.id.typeMarcher;
    } else if (dto) {
      typeMarcherId = { id: dto.typeMarcherId };
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Erreur',
        detail: this.translate.instant('settings.seuils.message.error.updated'),
      });
      return;
    }

    // Ensure acId is included in the DTO
    const updateDto = {
      ...dto,
      acId: this.acId,
    };

    this.settingsSeuilsService.updateSeuil(updateDto).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Succès',
          detail: this.translate.instant('settings.seuils.message.success.updated'),
        });
        this.getSeuils();
        this.updateSeuilComponent.toggleVisible();
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Erreur',
          detail: this.translate.instant('settings.seuils.message.error.updated'),
        });
      },
    });
  }
}
